import { action } from '@ember/object';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import MagicUrl from 'quill-magic-url';
import Quill from 'quill';
import '../../utils/custom-quill-mention';
import InitiativePill from '../../utils/initiative-pill';
import ImageUploader from 'quill2-image-uploader';
import ImageFormat from 'scaleflow/utils/quill-extenstions/image-format';
import imageResizeOptions from 'scaleflow/utils/quill-extenstions/image-resize';
import registerIfNotRegistered from 'scaleflow/utils/quill-extenstions/registerIfNotRegistered';
import imageUploadHandler from 'scaleflow/utils/quill-extenstions/image-uploader';

registerIfNotRegistered(Quill, 'modules/magicUrl', MagicUrl);
registerIfNotRegistered(Quill, 'modules/initiative-pill', InitiativePill);
registerIfNotRegistered(Quill, 'modules/imageUploader', ImageUploader);

Quill.register('formats/image', ImageFormat, true);

export default class MoleculesEditorComponent extends Component {
  @service('quill') quillService;
  @service('initiatives') initiativesService;
  @service('users') userService;
  @service initiativeSlideover;
  @service organisation;
  @service intl;
  @service session;
  theme = 'bubble';
  placeholder = '...';
  _singleLineMode = undefined;
  editorId;
  listenerEnabled = false;
  timer = null;

  get config() {
    const self = this;
    return {
      mention: {
        allowedChars: /^[A-Za-z0-9\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['@'],
        source: async function (searchTerm, renderList, mentionChar) {
          let values;

          if (mentionChar === '@') {
            const users = await self.userService.getUsers();
            const activeUsers = users.filter((user) => user.active);
            values = [...activeUsers, { value: 'all', id: 0 }];
          }

          if (searchTerm.length === 0) {
            renderList(values, searchTerm);
          } else {
            const matches = [];
            const regex = new RegExp(`^(${searchTerm})`, 'i');
            for (let i = 0; i < values.length; i++) {
              const result = values[i]?.value?.match?.(regex);
              if (result) matches.push(values[i]);
            }
            renderList(matches, searchTerm);
          }
        },
        positioningStrategy: 'fixed',
        renderLoading: function () {
          return '<span class="p-2 text-gray-700">Loading...</span>';
        },
      },
    };
  }

  get initiativeBlotConfig() {
    const self = this;
    return {
      config: {
        allowedChars: /^[A-Za-z0-9\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['+'],
        source: async function (searchTerm, renderList) {
          let values = await self.initiativesService.getInitiatives();
          if (searchTerm.length === 0) {
            renderList(values, searchTerm);
          } else {
            const matches = [];
            const regex = new RegExp(`^(${searchTerm})`, 'i');
            for (let i = 0; i < values.length; i++) {
              const result =
                values[i].title.match(regex) ?? values[i].abbv.match(regex);
              if (result) matches.push(values[i]);
            }
            renderList(matches, searchTerm);
          }
        },
        positioningStrategy: 'fixed',
        showDenotationChar: false,
        renderLoading: function () {
          return '<span class="p-2 text-gray-700">Loading...</span>';
        },
      },
    };
  }

  get singleLineMode() {
    return this._singleLineMode ?? this.args.singleLineMode ?? false;
  }

  @action
  singleLineModeEnabled() {
    return this.singleLineMode;
  }

  get editor() {
    return this.quillService.instance(this.editorId);
  }

  get isEnabled() {
    return this.args.active;
  }

  setSelection(oldRange) {
    // debugger;
    setTimeout(() => {
      try {
        if (this.editor !== null) {
          this.editor.container.setAttribute('data-cyeditor', 'focus');
          this.editor.setSelection({ ...oldRange }, Quill.sources.USER);
        }
      } catch (err) {
        console.log('ERROR HERE', err);
      }
    }, 0);
    return;
  }

  @action
  activate(element, values) {
    let active = values;
    if (active && active != this.active) {
      this.editor.setSelection(0, 0);
    }
  }

  @action
  debouncedSave() {
    return this.debounce(() => this.saveItemWhenDirty(), 1500);
  }

  @action
  debounce(func, timeout = 2000) {
    clearTimeout(this.timer);
    this.timer = setTimeout(func, timeout);
  }

  @action
  setDelta(delta) {
    if (this.args.item.currentState.stateName !== 'root.deleted.inFlight') {
      this.args.item.set('text_delta', delta);
      this.debouncedSave();
    }
  }

  @action
  handleTags(delta) {
    let tags = [];
    let mentions = [];
    let _initiatives = [];
    const filtrableText = (delta?.ops || []).filter((op) => {
      return typeof op.insert === 'string' || op.insert.mention;
    });

    if (filtrableText.length == 0) return [tags, mentions, _initiatives];

    delta.ops.forEach((item) => {
      if (typeof item.insert === 'string') {
        let extracts = item.insert.match(/#\w*/g);
        if (extracts) {
          extracts = extracts.map((item) => item.substring(1));
          tags = [...tags, ...extracts];
        }
      }
      if (item.insert.mention) {
        if (item.insert.mention.denotationChar === '#') {
          tags.push(item.insert.mention.value);
        }
        if (item.insert.mention.denotationChar === '@') {
          mentions.push(item.insert.mention.value);
        }
      }
    });

    return [tags, mentions, _initiatives];
  }

  @action
  async saveItemWhenDirty() {
    if (
      this.args.item.isDestroying ||
      this.args.item.isDestroyed ||
      this.args.item.isDeleted
    )
      return;

    const changedAttributesLength = Object.keys(
      this.args.item.changedAttributes() ?? {},
    ).length;
    if (changedAttributesLength === 0) return;

    Promise.resolve(this.args.item.save());
  }

  @action
  handleSelectionChange(oldRange, newRange) {
    if (
      oldRange === null &&
      newRange !== null &&
      !(newRange.index == 0 && newRange.length == 0) &&
      !this.editor.hasFocus()
    ) {
      this.debouncedSave();
      this.restoreSingleLineMode();
      return;
    }
    if (oldRange !== null && newRange === null) {
      if (this.args.onActivate) {
        var propagation = this.args.onActivate();
      }
      if (propagation == false) {
        return;
      } else {
        this.setSelection(oldRange);
      }
    }
  }

  @action
  async insertNew(element) {
    this.editorId = element.id;
  }

  get events() {
    let events = {
      moveUp: this.args.onUp ?? this._logIntent('moveUp'),
      moveDown: this.args.onDown ?? this._logIntent('moveDown'),
      fastExit: this.args.onFastExit ?? this._logIntent('fastExit'),
      emptyTab: this.args.onEmptyTab ?? this._logIntent('emptyTab'),
      tab: this.args.onTab ?? this._logIntent('tab'),
      delete: this.args.onDelete ?? this._logIntent('delete'),
      blur: this.onBlur,
    };

    return events;
  }

  get keyBindingEvents() {
    if (this.args.events) {
      return { ...this.events, ...this.args.events };
    } else {
      return this.events;
    }
  }

  @action
  onBlur() {
    this.editor.blur();
    this.restoreSingleLineMode();
  }

  @action
  restoreSingleLineMode() {
    this._singleLineMode = this.args.singleLineMode ?? false;
  }

  @action
  disableSingleLineMode() {
    this._singleLineMode = false;
  }

  get keyBindings() {
    const self = this;
    let events = this.keyBindingEvents;
    let singleLineModeEnabled = this.singleLineModeEnabled;
    let disableSingleLineMode = this.disableSingleLineMode;
    let restoreSingleLineMode = this.restoreSingleLineMode;

    const dropdownIsOpen = () => {
      if (document.querySelector('.ql-mention-list-container')) {
        return true;
      }
    };

    let bindings = {
      up: {
        key: 'ArrowUp',
        handler: function (range, context) {
          if (dropdownIsOpen()) {
            return true;
          }
          const length = this.quill.getLength();
          if (
            (context.offset == 0 && range.index == 0) ||
            range.index == length - 1
          ) {
            events.blur();
            events.moveUp();
            return false;
          }
          return true;
        },
      },
      down: {
        key: 'ArrowDown',
        handler: function (range) {
          if (dropdownIsOpen()) {
            return true;
          }
          const length = this.quill.getLength();
          if (range.index == length - 1) {
            events.blur();
            events.moveDown();
            return false;
          }
          return true;
        },
      },
      backspace: {
        key: 'Backspace',
        handler: function (range, context) {
          if (dropdownIsOpen()) {
            return true;
          }
          if (
            context.offset == 0 &&
            range.index == 0 &&
            this.quill.getLength() == 1
          ) {
            clearTimeout(self.timer);
            events.delete();
            events.moveUp();

            return false;
          }
          if (context.empty === true) restoreSingleLineMode();
          return true;
        },
      },
      saveAndExit: {
        key: 'Enter',
        shortKey: true,
        handler: function () {
          if (dropdownIsOpen()) {
            return true;
          }
          events.blur();
          events.fastExit();
          return false;
        },
      },
      singleLineExit: {
        key: 'Enter',
        handler: function () {
          if (singleLineModeEnabled()) {
            events.blur();
            events.fastExit();
            return false;
          }
          return true;
        },
      },
      disableSingleLineExit: {
        key: 'Enter',
        shiftKey: true,
        handler: function () {
          disableSingleLineMode();
          return true;
        },
      },
      EmptyTab: {
        key: 'Tab',
        empty: true,
        handler: function () {
          if (dropdownIsOpen()) {
            return true;
          }
          events.blur();
          events.emptyTab();
          return false;
        },
      },
      tab: {
        key: 'Tab',
        // format: {
        //   list: false,
        // },
        handler: function () {
          if (dropdownIsOpen()) {
            return true;
          }
          events.blur();
          events.tab();
          return false;
        },
      },
    };

    return bindings;
  }

  get modules() {
    let modules = {};
    if (this.args._toolbar) {
      modules.toolbar = this.args._toolbar;
    } else {
      let toolbarOptions = [
        ['bold', 'italic', 'strike'], // toggled buttons
        ['blockquote', 'code-block'],

        [{ header: 1 }, { header: 2 }], // custom button values
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        ['link', 'image', 'clean'], // remove formatting button
      ];
      modules.toolbar = toolbarOptions;
    }

    modules.keyboard = {
      bindings: this.keyBindings,
    };

    modules.clipboard = {
      matchVisual: false,
    };

    modules.syntax = this.args.syntax ?? true;
    modules.magicUrl = true;

    modules.mention = this.config.mention;
    modules['initiative-pill'] = this.initiativeBlotConfig.config;

    // console.log(this.quillService);

    modules.imageUploader = {
      upload: (file) => {
        return imageUploadHandler(
          this.session.idToken,
          this.organisation.id,
          file,
        ).then((s3Link) => {
          return s3Link;
        });
      },
    };

    modules.resize = imageResizeOptions(this.intl);

    return modules;
  }

  get bounds() {
    return `#${this.editorId}`;
  }

  _logIntent() {
    if (this.args.debug) {
      return () => {};
    } else {
      return () => {};
    }
  }
}
